import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '@environments/environment';

export interface DashboardSummary {
  id: string;
  name: string;
  location: string;
  programId?: string | null;
  programName?: string | null;
  isDefault?: boolean;
  liveId?: string | null;
}

export type DashboardScope =
  | { location: 'home' }
  | { location: 'program'; programId: string };

interface DashboardApiResponse {
  dashboards?: DashboardSummary[];
  count?: number;
  defaultDashboardId?: string | null;
}

interface ScopedDashboards {
  dashboards: DashboardSummary[];
  defaultDashboardId: string | null;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private cache = new Map<string, Observable<ScopedDashboards>>();

  constructor(private http: HttpClient) {}

  getDashboards(orgId: string, scope: DashboardScope): Observable<DashboardSummary[]> {
    return this.load(orgId, scope).pipe(map((scoped) => scoped.dashboards));
  }

  hasDashboards(orgId: string, scope: DashboardScope): Observable<boolean> {
    return this.getDashboards(orgId, scope).pipe(map((dashboards) => dashboards.length > 0));
  }

  getInitialDashboardId(orgId: string, scope: DashboardScope): Observable<string | null> {
    return this.load(orgId, scope).pipe(
      map(({ dashboards, defaultDashboardId }) => {
        if (scope.location === 'home' && defaultDashboardId) {
          return defaultDashboardId;
        }
        const flaggedDefault = dashboards.find((dashboard) => dashboard.isDefault);
        return flaggedDefault?.id ?? dashboards[0]?.id ?? null;
      }),
    );
  }

  private load(orgId: string, scope: DashboardScope): Observable<ScopedDashboards> {
    const key = this.cacheKey(orgId, scope);
    if (!this.cache.has(key)) {
      const url = `${environment.dashboardBuilderUrl}/api/orgs/${orgId}/dashboards`;
      const scoped$ = this.http.get<DashboardApiResponse>(url, { params: this.params(scope) }).pipe(
        map((response) => ({
          dashboards: response?.dashboards ?? [],
          defaultDashboardId: response?.defaultDashboardId ?? null,
        })),
        shareReplay(1),
      );
      this.cache.set(key, scoped$);
    }
    return this.cache.get(key)!;
  }

  private params(scope: DashboardScope): HttpParams {
    const params = new HttpParams().set('location', scope.location);
    return scope.location === 'program' ? params.set('programId', scope.programId) : params;
  }

  private cacheKey(orgId: string, scope: DashboardScope): string {
    return `${orgId}|${scope.location}|${scope.location === 'program' ? scope.programId : ''}`;
  }
}
